"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import ProjectImageCarousel from "@/components/ui/ProjectImageCarousel";
import NewsCard from "@/components/shared/NewsCard";
import { fetchApi, getApiUrl } from "@/lib/api-utils";
import { getImageUrl } from "@/utils/utils";
import RichContent from "@/components/ui/RichContent";
import ErrorBoundary from "../error-boundary";

interface ProjectDetailClientProps {
  slug: string;
  lang: string;
  initialProject?: ProjectApiResponse | any;
}

interface ProjectDetailImage {
  id: number;
  documentId: string;
  name: string;
  alternativeText: string | null;
  caption: string | null;
  width: number;
  height: number;
  formats: {
    thumbnail: {
      url: string;
      width: number;
      height: number;
    };
  };
  url: string;
}

interface ProjectDetailFeature {
  id: number;
  text: string;
}

interface ProjectDetailRelatedProject {
  id: number;
  documentId: string;
  title: string;
  excerpt: string;
  slug: string;
  image: ProjectDetailImage;
}

interface ProjectDetailData {
  id: number;
  documentId: string;
  title: string;
  slug: string;
  excerpt: string;
  description: string;
  fundingSource: string;
  startDate: string;
  endDate: string | null;
  servicesDescription: string;
  images: ProjectDetailImage[];
  features: ProjectDetailFeature[];
  relatedProjects: ProjectDetailRelatedProject[];
}

interface ProjectApiResponse {
  data: {
    id: number;
    documentId: string;
    title: string;
    breadcrumb: {
      home: string;
      projects: string;
    };
    relatedProjectsText: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
    locale: string | null;
    details: ProjectDetailData;
  };
}

export default function ProjectDetailClient({
  slug,
  lang,
  initialProject,
}: ProjectDetailClientProps) {
  const router = useRouter();
  // Extract the actual project data from the API response structure
  const extractedProject = initialProject?.data?.details || initialProject;
  const [project, setProject] = useState<ProjectDetailData | null>(
    extractedProject || null
  );
  const [loading, setLoading] = useState(!initialProject);
  const [error, setError] = useState<string | null>(null);
  const [currentRelatedPage, setCurrentRelatedPage] = useState(0);

  // Animation variants
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 60,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "circOut" as const,
      },
    },
  };

  const staggerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  };

  useEffect(() => {
    const fetchProject = async () => {
      if (initialProject) return;

      try {
        setLoading(true);
        setError(null);
        const url = getApiUrl(`/project-details-page/${slug}`);
        const response = await fetchApi<ProjectApiResponse>(url);

        if (response?.data?.details) {
          setProject(response.data.details);
        } else {
          setError("Project not found");
        }
      } catch (err) {
        setError("Failed to load project");
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [slug, initialProject]);

  const projectImages =
    (project?.images?.length > 0 &&
      project.images
        .filter((img) => img && img.url)
        .map((img) => {
          return getImageUrl(img.url);
        })) ||
    [];

  const getProjectStatus = () => {
    if (!project) return "Unknown";

    if (!project.endDate) {
      return "Ongoing";
    }

    const endDate = new Date(project.endDate);
    const today = new Date();

    if (endDate > today) {
      return "Ongoing";
    }
    return endDate.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
  };

  // Pagination logic for related projects
  const relatedProjects = project?.relatedProjects || [];
  const projectsPerPage = 3;
  const totalRelatedPages = Math.ceil(relatedProjects.length / projectsPerPage);
  const displayedRelatedProjects = relatedProjects.slice(
    currentRelatedPage * projectsPerPage,
    (currentRelatedPage + 1) * projectsPerPage
  );

  const nextRelatedPage = () => {
    setCurrentRelatedPage((prev) => (prev + 1) % totalRelatedPages);
  };

  const previousRelatedPage = () => {
    setCurrentRelatedPage(
      (prev) => (prev - 1 + totalRelatedPages) % totalRelatedPages
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a5852] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading project...</p>
        </div>
      </div>
    );
  }

  // Show error state if data fetching failed
  if (error || !project) {
    return <ErrorBoundary />;
  }

  return (
    <main>
      <motion.section
        className="py-12 relative"
        initial="hidden"
        animate="visible"
        variants={sectionVariants}
      >
        {/* Background Image */}
        <div className="mx-auto relative z-10">
          {/* Project Header and Main Content Section with Background */}
          <div className="relative">
            {/* Background Image for this specific section */}
            <Image
              src="/images/shared/page-blend-bg.svg"
              alt="Background pattern"
              fill
              className="object-cover object-center opacity-100 pointer-events-none select-none"
            />

            <div className="relative group/progress">
              <div className="p-4 sm:p-6 lg:p-[50px]">
                {/* Project Header */}
                <motion.div className="mb-8" variants={itemVariants}>
                  <div className="bg-[#1A5852] text-white px-5 py-3 rounded-lg inline-block w-full">
                    <h1 className="text-xl sm:text-2xl lg:text-[28px] font-semibold leading-tight tracking-[-0.72px]">
                      {project.title}
                    </h1>
                  </div>
                </motion.div>

                {/* Main Content Section */}
                {/* Main Content Grid */}
                <motion.div
                  className="grid grid-cols-1 lg:grid-cols-[500px_1fr] gap-6 lg:gap-8"
                  variants={staggerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {/* Image Carousel Section */}
                  <motion.div variants={itemVariants}>
                    <ProjectImageCarousel
                      images={projectImages}
                      title={project.title}
                    />
                  </motion.div>

                  {/* Content Section */}
                  <motion.div
                    className="border border-[#DEE1E5] rounded-lg bg-white p-6 lg:p-7 flex flex-col justify-start"
                    variants={itemVariants}
                  >
                    <div className="space-y-6 lg:space-y-7">
                      {/* Status Tags */}
                      <div className="w-full">
                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-0">
                          <div className="relative flex-[1.2] sm:flex-[1.4]">
                            <div className="bg-[#603913] text-white px-5 py-4 sm:py-[18px] rounded-lg sm:rounded-none sm:clip-path-angled-right text-center lg:text-left">
                              <span className="text-lg sm:text-xl lg:text-[24px] font-semibold leading-6 tracking-[-0.72px]">
                                {project.fundingSource}
                              </span>
                            </div>
                          </div>
                          <div className="relative flex-[1.2] sm:flex-[1.2]">
                            <div className="bg-[#262262] text-white px-5 py-4 sm:py-[18px] rounded-lg sm:rounded-none sm:clip-path-angled-left text-center">
                              <span className="text-lg sm:text-xl lg:text-[24px] font-semibold leading-6 tracking-[-0.72px]">
                                {new Date(project.startDate).toLocaleDateString(
                                  "en-US",
                                  { month: "short", year: "numeric" }
                                )}{" "}
                                - {getProjectStatus()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Content Sections */}
                      <div className="space-y-6 lg:space-y-8">
                        {/* Description */}
                        <div className="space-y-3">
                          <h2 className="text-[#1A5852] text-lg sm:text-xl font-semibold leading-7 tracking-[-0.72px]">
                            Description
                          </h2>
                          <div className="w-full h-0.5 bg-[#F4F5F6]"></div>

                          <RichContent
                            html={project.description}
                            className="text-[#3D434C] text-base leading-[26px]"
                          />
                        </div>

                        {/* Main Project Features */}
                        {project.features && project.features.length > 0 && (
                          <div className="space-y-3">
                            <h2 className="text-[#1A5852] text-lg sm:text-xl font-semibold leading-7 tracking-[-0.72px]">
                              Main Project Features
                            </h2>
                            <div className="w-full h-0.5 bg-[#F4F5F6]"></div>
                            <div className="text-[#3D434C] text-base leading-[26px] space-y-1">
                              {project.features.map((feature, index) => (
                                <p key={feature.id || index}>
                                  • {feature.text}
                                </p>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Services */}
                        <div className="space-y-3">
                          <h2 className="text-[#1A5852] text-lg sm:text-xl font-semibold leading-7 tracking-[-0.72px]">
                            Services
                          </h2>
                          <div className="w-full h-0.5 bg-[#F4F5F6]"></div>
                          <RichContent
                            html={project.servicesDescription}
                            className="text-[#3D434C] text-base leading-[26px]"
                          />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              </div>

              {/* Progress Bar */}
              <div className="flex h-[2px] bg-gray-25 relative">
                <div
                  className={`h-[2px] bg-[#1c8579] absolute left-0 top-0 transition-all duration-500 w-0 group-hover/progress:w-full`}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Projects Section */}
        {relatedProjects.length > 0 && (
          <div className="bg-white relative  group/progress">
            {/* Background Image */}
            <Image
              src="/images/shared/details-bottom-bg.png"
              alt="Background pattern"
              fill
              className="object-cover object-bottom opacity-100 mix-blend-luminosity pointer-events-none select-none"
            />

            <div className="relative p-4 sm:p-6 lg:p-[50px]">
              {/* Section Header */}
              <div className="flex items-center gap-[10px] pb-2 border-b border-[#f4f5f6] mb-6">
                <h2 className="text-[#1a1d21] text-[28px] font-medium leading-[34px] tracking-[-0.72px]">
                  Related Projects
                </h2>
              </div>

              {/* Related Projects Grid */}
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-6"
                variants={staggerVariants}
                initial="hidden"
                animate="visible"
              >
                {displayedRelatedProjects.map((relatedProject, index) => (
                  <motion.div
                    key={`${relatedProject.id}-${index}`}
                    variants={itemVariants}
                  >
                    <NewsCard
                      image={
                        relatedProject.image?.url
                          ? getImageUrl(relatedProject.image.url)
                          : "/images/shared/empty-bg.jpg"
                      }
                      title={relatedProject.title}
                      excerpt={relatedProject.excerpt || ""}
                      alt={relatedProject.title}
                      href={`/${lang}/projects/${relatedProject.slug}`}
                      type="PROJECTS"
                    />
                  </motion.div>
                ))}
              </motion.div>

              {/* Navigation Arrows */}
              {totalRelatedPages > 1 && (
                <motion.div
                  className="flex items-center gap-6 justify-center"
                  variants={itemVariants}
                >
                  <button
                    onClick={previousRelatedPage}
                    className="flex items-center justify-center w-[46px] h-[46px] p-[13px] border border-[#88909f] rounded-[25px] hover:bg-[#f4f5f6] transition-colors cursor-pointer"
                  >
                    <svg width="20" height="20" viewBox="0 0 21 20" fill="none">
                      <path
                        d="M8.4763 4.94165L3.41797 9.99998L8.4763 15.0583"
                        stroke="#88909F"
                        strokeWidth="1.25"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M17.5836 10H3.55859"
                        stroke="#88909F"
                        strokeWidth="1.25"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={nextRelatedPage}
                    className="flex items-center justify-center w-[46px] h-[46px] p-[13px] border border-[#88909f] rounded-[25px] hover:bg-[#f4f5f6] transition-colors cursor-pointer"
                  >
                    <svg width="20" height="20" viewBox="0 0 21 20" fill="none">
                      <path
                        d="M12.5234 4.94165L17.5818 9.99998L12.5234 15.0583"
                        stroke="#88909F"
                        strokeWidth="1.25"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M3.41797 10H17.443"
                        stroke="#88909F"
                        strokeWidth="1.25"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </motion.div>
              )}
            </div>

            {/* Progress Bar */}
            <div className="flex h-[2px] bg-gray-25 relative">
              <div
                className={`h-[2px] bg-[#1c8579] absolute left-0 top-0 transition-all duration-500 w-0 group-hover/progress:w-full`}
              ></div>
            </div>
          </div>
        )}
      </motion.section>
    </main>
  );
}
